import { Injectable } from "@angular/core";
import { Observable, BehaviorSubject, of } from 'rxjs';
import { filter, distinctUntilChanged, switchMap, tap, catchError, map, retry } from 'rxjs/operators';
import { ServerTokenService } from "../services/server-token.service";
import { CustomAuthServiceService } from "../auth/custom-auth-service.service";
import { ClientSettings, ClientSettingsUpdateBody, CLIENT_SETTINGS_DEFAULTS, cloneClientSettings, GATEWAY_REFERRAL_LINKS_DISABLED_KEY, isDischargePlanEnabled, isGatewayReferralLinksDisabled, isScheduledEmailsEnabled } from "./client-settings.types";
import { localStorageGet, localStorageSet } from "../utils";
import { SettingsService } from "./settings.service";
import { DISCHARGE_PLAN_FLAG_KEY } from "./discharge-plan/discharge-plan-entitlement";
import { SCHEDULED_EMAILS_FLAG_KEY } from "../admin/scheduled-emails/scheduled-emails-entitlement";

const NAV_FLAG_KEY = 'nav.newNavigation';
// Same problem, same fix: the RiskAI entitlement is read synchronously by
// HighRiskRouteGuard and by BedBoardComponent's field initializer, both of which
// run before this service's HTTP load can answer. Cache the one key.
const RISK_FLAG_KEY = 'nav.riskProduct';
// And the paid-option flags (Discharge Plan, Scheduled Emails), read
// synchronously by the nav model, which cannot inject this service
// (auth -> nav -> settings -> auth cycle), and by components that run before
// the load. Opt-in like RiskAI; nothing is seeded into the settings object on
// boot because every reader goes through the cache itself, and the route
// guards wait for the first load before they decide.

// newNavigation gates the whole nav shell and is read synchronously at component
// construction, one tick before this service's HTTP load can answer. Caching just that
// one key and seeding it here is what makes the read correct on every load after login.
function seededDefaults(legacyOverride: boolean): ClientSettings {
  const defaults = cloneClientSettings(CLIENT_SETTINGS_DEFAULTS);
  return {
    ...defaults,
    uiSettings: {
      ...defaults.uiSettings,
      newNavigation: !legacyOverride && localStorageGet(NAV_FLAG_KEY) === true,
      // Opt-in: anything other than a cached true reads as no entitlement.
      products: { ...defaults.uiSettings.products, risk: localStorageGet(RISK_FLAG_KEY) === true },
    },
  };
}

@Injectable({
  providedIn: 'root'
})
export class ClientSettingsService {

  private settingsSubject: BehaviorSubject<ClientSettings>;
  settings$: Observable<ClientSettings>;

  private ready: Promise<void>;
  private resolveReady: () => void;

  constructor(
    private server: ServerTokenService,
    private auth: CustomAuthServiceService,
    private settingsService: SettingsService,
  ) {
    this.settingsSubject = new BehaviorSubject<ClientSettings>(seededDefaults(auth.legacyNavigation));
    this.settings$ = this.settingsSubject.asObservable();
    this.armReady();
    auth.authState$.pipe(
      distinctUntilChanged(),
      tap(state => {
        if (!state) {
          this.resetToDefaults();
          return;
        }
        this.applyLegacyOverride();
      }),
      filter(state => !!state),
      switchMap(() => server.getWithToken<Partial<ClientSettings>>('snfs/getClientSettings', {}).pipe(
        retry(2),
        catchError(err => {
          console.error('clientSettings initial load failed', err?.status, err?.statusText);
          return of(null);
        }),
      )),
    ).subscribe((res) => {
      if (res) {
        this.mergeSettings(res);
      }
      // Unconditional: the pipe's catchError turns a failed load into null, and a route
      // waiting on this must activate on failure rather than stall for the full timeout.
      this.resolveReady();
    });
  }

  get settings(): ClientSettings {
    return this.settingsSubject.value;
  }

  /**
   * Resolves once the first settings load after login has settled — success or failure.
   * Used by ClientSettingsReadyResolver to hold the route until `newNavigation` is known
   * on the one path the localStorage cache cannot cover: the first load after a logout
   * cleared it. Re-armed on logout, since login is an SPA navigation and this service is
   * a singleton that would otherwise stay resolved from the previous session.
   */
  waitUntilReady(): Promise<void> {
    return this.ready;
  }

  private armReady(): void {
    this.ready = new Promise<void>((resolve) => { this.resolveReady = resolve; });
  }

  refresh(): Observable<void> {
    return this.server.getWithToken<Partial<ClientSettings>>('snfs/getClientSettings', {}).pipe(
      tap(res => {
        if (res) this.mergeSettings(res);
      }),
      catchError(err => {
        console.error('clientSettings refresh failed', err?.status, err?.statusText);
        return of(null);
      }),
      map(() => void 0),
    );
  }

  loadAdmin(): Observable<ClientSettings | null> {
    return this.server.getWithToken<Partial<ClientSettings>>('snfs/admin/getClientSettings').pipe(
      tap(res => {
        if (res) this.mergeSettings(res);
      }),
      map(res => res ? this.settingsSubject.value : null),
    );
  }

  updateAdmin(body: ClientSettingsUpdateBody): Observable<ClientSettings> {
    return this.server.postWithToken<Partial<ClientSettings>>('snfs/admin/updateClientSettings', body).pipe(
      tap(res => {
        if (res) this.mergeSettings(res);
      }),
      map(() => this.settingsSubject.value),
    );
  }

  /**
   * Merge a partial `uiSettings` patch into the cached settings and notify
   * subscribers. Callers must go through this rather than mutating the object
   * returned by `settings` — an in-place write updates the cache silently and
   * `settings$` never emits.
   */
  patchUiSettings(patch: Partial<ClientSettings['uiSettings']>): void {
    const current = this.settingsSubject.value;
    this.settingsSubject.next({
      ...current,
      uiSettings: { ...current.uiSettings, ...patch },
    });
  }

  /** One localStorage write per changed flag; a matching value writes nothing. */
  private cacheFlag(key: string, value: boolean): void {
    if (localStorageGet(key) !== value) {
      localStorageSet(key, value);
    }
  }

  private applyLegacyOverride(): void {
    const current = this.settingsSubject.value;
    const newNavigation = !this.auth.legacyNavigation && localStorageGet(NAV_FLAG_KEY) === true;
    if (current.uiSettings.newNavigation === newNavigation) {
      return;
    }
    this.settingsSubject.next({
      ...current,
      uiSettings: { ...current.uiSettings, newNavigation },
    });
  }

  private resetToDefaults(): void {
    this.settingsSubject.next(seededDefaults(this.auth.legacyNavigation));
    this.armReady();
  }

  private mergeSettings(res: Partial<ClientSettings>): void {
    const regionNavigation = !!res.uiSettings?.newNavigation;
    if (localStorageGet(NAV_FLAG_KEY) !== regionNavigation) {
      localStorageSet(NAV_FLAG_KEY, regionNavigation);
    }
    // Opt-in read: only an explicit true from the region entitles the client.
    const regionRisk = res.uiSettings?.products?.risk === true;
    if (localStorageGet(RISK_FLAG_KEY) !== regionRisk) {
      localStorageSet(RISK_FLAG_KEY, regionRisk);
    }
    // Opt-in reads: only an explicit true from the region grants a feature.
    // Written BEFORE the subject emits, so a nav re-resolve triggered by this
    // very emission already sees the new values through the entitlement readers.
    this.cacheFlag(DISCHARGE_PLAN_FLAG_KEY, isDischargePlanEnabled(res.uiSettings));
    this.cacheFlag(SCHEDULED_EMAILS_FLAG_KEY, isScheduledEmailsEnabled(res.uiSettings));
    this.cacheFlag(GATEWAY_REFERRAL_LINKS_DISABLED_KEY, isGatewayReferralLinksDisabled(res.uiSettings));
    const newNavigation = !this.auth.legacyNavigation && regionNavigation;
    const current = this.settingsSubject.value;
    this.settingsSubject.next({
      ...current,
      ...res,
      uiSettings: {
        // Defaults, not `current`: the server sends the whole subtree, and carrying the
        // session value forward pinned keys the region had since unset.
        ...CLIENT_SETTINGS_DEFAULTS.uiSettings,
        ...res.uiSettings,
        newNavigation,
        // Verbatim, NOT coerced: the admin screen must see the region's real tri-state
        // (undefined = never configured), or its save materializes an explicit false.
        products: { ...res.uiSettings?.products },
        newVersionPages: {
          ...current.uiSettings.newVersionPages,
          ...res.uiSettings?.newVersionPages,
        },
      },
    });
    this.settingsService.setRegionTimezone(res.uiSettings?.timezone);
  }
}
